import { trackContentEngagement } from "@/lib/analytics";
import { useEffect } from "react";

import SurfaceCard from "@/components/primitives/SurfaceCard";
import { SectionHead } from "@/components/organization/kit";
import { parseVideoUrl } from "@/lib/videoEmbed";

interface VideoPlayerProps {
  thumbnailUrl?: string;
  title?: string;
  videoUrl?: string;
  orgId?: string;
  /**
   * The organization's publication state, already loaded by the profile page.
   * `content_engagement_events` only accepts an insert for a published
   * organization, so an unpublished draft is suppressed before the write rather
   * than sent to be refused — which also keeps an admin previewing their own
   * draft out of the org's engagement numbers.
   */
  isPublished?: boolean;
}

const VideoPlayer = ({ videoUrl, orgId, isPublished }: VideoPlayerProps) => {
  // Same parser as the editor, so anything an admin could save can be played.
  const embedSrc = parseVideoUrl(videoUrl)?.embedUrl ?? null;

  useEffect(() => {
    if (orgId && embedSrc) {
      void trackContentEngagement(orgId, 'video_play', {
        isPublished: isPublished === true,
        sectionId: 'culture',
      });
    }
  }, [orgId, embedSrc, isPublished]);

  if (!embedSrc) return null;

  return (
    <SurfaceCard padding="lg" className="rounded-2xl">
      <SectionHead kicker="See us in action" title="Inside the team" />
      <div className="relative w-full overflow-hidden rounded-[var(--radius-md)]" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={embedSrc}
          className="absolute left-0 top-0 h-full w-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Showcase video"
        />
      </div>
    </SurfaceCard>
  );
};

export default VideoPlayer;
